import { useState, useMemo } from "react";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES, TREATMENTS, buildWhatsAppLink } from "@/data/lakaData";

const FILTERS = [
  { key: "all", label: "Todos" },
  ...CATEGORIES.map((c) => ({ key: c.key, label: c.short })),
];

const Pricing = () => {
  const [active, setActive] = useState("all");

  const list = useMemo(
    () => (active === "all" ? TREATMENTS : TREATMENTS.filter((t) => t.cat === active)),
    [active],
  );

  const goTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const reserve = (name, price) => {
    const wa = buildWhatsAppLink(
      `Hola LAKA, me gustaría reservar: ${name} (${price}€). ¿Me indicáis disponibilidad?`,
    );
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="precios"
      data-testid="pricing-section"
      className="py-24 md:py-32"
      style={{ background: "#FBF6F2" }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14">
          <span className="laka-eyebrow">Precios iniciales</span>
          <h2 className="mt-4 font-serif-laka text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] text-[#1A1A1A]">
            Transparencia <span className="italic text-[#D6B36A]">y cuidado del detalle</span>
          </h2>
          <p className="mt-5 text-sm md:text-base text-[#1A1A1A]/70 max-w-xl mx-auto leading-relaxed font-light">
            Precios iniciales orientativos. Cada plan se personaliza tras la
            valoración con nuestro equipo.
          </p>
        </div>

        {/* Filters */}
        <div
          data-testid="pricing-filters"
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`pricing-filter-${f.key}`}
              onClick={() => setActive(f.key)}
              className={`px-5 py-2.5 text-[0.68rem] tracking-[0.22em] uppercase font-medium border transition-all duration-300 ${
                active === f.key
                  ? "bg-[#1A1A1A] text-[#FBF6F2] border-[#1A1A1A]"
                  : "bg-transparent text-[#1A1A1A] border-[#1A1A1A]/20 hover:border-[#D6B36A] hover:text-[#D6B36A]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((t, i) => (
            <div
              key={`${t.cat}-${t.name}`}
              data-testid={`pricing-item-${i}`}
              className={`relative p-6 laka-card ${
                t.featured ? "border-[#D6B36A]" : ""
              }`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-6 bg-[#D6B36A] text-[#1A1A1A] px-3 py-1 text-[0.55rem] tracking-[0.3em] uppercase font-medium">
                  Destacado
                </div>
              )}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#D6B36A] font-medium">
                    {CATEGORIES.find((c) => c.key === t.cat)?.short}
                  </div>
                  <h3 className="mt-2 font-serif-laka text-xl text-[#1A1A1A] leading-snug">
                    {t.name}
                  </h3>
                  <div className="mt-1 text-[0.72rem] tracking-[0.2em] uppercase text-[#1A1A1A]/50">
                    {t.duration}
                  </div>
                </div>
                <div className="font-serif-laka text-3xl text-[#1A1A1A] shrink-0">
                  {t.price}€
                </div>
              </div>
              <div className="mt-5 flex items-center gap-2">
                <button
                  data-testid={`pricing-reserve-${i}`}
                  onClick={() => reserve(t.name, t.price)}
                  className="flex-1 py-2.5 text-[0.68rem] tracking-[0.22em] uppercase font-medium border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FBF6F2] transition-colors"
                >
                  Reservar
                </button>
                <button
                  onClick={() => goTo("contacto")}
                  aria-label="Solicitar por formulario"
                  className="w-10 h-10 flex items-center justify-center border border-[#1A1A1A]/20 hover:border-[#D6B36A] hover:text-[#D6B36A] transition-colors"
                >
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
